import { FormattedMessage } from 'react-intl'
import { useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import FormLayout from 'features/App/components/FormLayout'
import { withLastUrl } from 'features/App/components/WithLastUrl'

import { CreateRule } from 'features/Loyalty/components/CreateRule'
import { CREATE_LOYALTY_CAMPAIGN_RULE } from 'features/Loyalty/mutations-legacy'
import { GET_LOYALTY_CAMPAIGN_COMPANY } from 'features/Loyalty/queries-legacy'
import { integerToCents } from 'utils/number'
import { ReactElement } from 'react'
import { ICreateRuleForm } from 'features/Loyalty/components/CreateRule/types'
import { getCitySegmentsForMutation } from 'features/BaseRules/utils'

const NewRuleView = (): ReactElement => {
  const { campaignId } = useParams()
  const navigate = useNavigate()

  const {
    data: queryCompanyData,
    error: errorQueryCompany,
    loading: loadingQueryCompany,
    refetch,
    networkStatus
  } = useQuery(GET_LOYALTY_CAMPAIGN_COMPANY, {
    variables: {
      campaignId
    }
  })
  const [createRule, { error, loading }] = useMutation(CREATE_LOYALTY_CAMPAIGN_RULE, {
    onCompleted() {
      navigate(`/loyalty/campaigns/${campaignId}/rules`)
    },
    onError() {
      // Do nothing
    }
  })

  function handleCreateSubmit(values: ICreateRuleForm): void {
    const {
      ruleName,
      ruleDescription,
      ruleDetails,
      ruleHowToGetIt,
      ruleValidAt,
      segments,
      allCitiesAndSegments,
      totalBookingValue,
      ruleEffects,
      salesChannels,
      cityOrigins,
      cityDestinations,
      allSegments,
      isReturn,
      routes,
      services
    } = values

    const citySegments = getCitySegmentsForMutation({
      allCitiesAndSegments,
      allSegments,
      cityOrigins,
      cityDestinations,
      segments,
      isReturn
    })

    createRule({
      variables: {
        campaignId,
        ruleName,
        ruleDescription,
        ruleHowToGetIt,
        ruleDetails,
        ruleValidAt,
        ruleEffects: {
          rewardID: ruleEffects.reward.id
        },
        ruleConditions: {
          salesChannelIDs: salesChannels.map(({ id }) => id),
          totalBookingsValue: integerToCents(totalBookingValue),
          citySegments,
          routeIDs: routes.map(({ id }) => id),
          routeServiceIDs: services.map(({ id }) => id)
        }
      }
    })
  }
  let content = <></>
  if (networkStatus === NetworkStatus.refetch || loadingQueryCompany) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (errorQueryCompany) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="loyaltyCampaign.rules.create.action.reload"
              defaultMessage="There was an error try again later"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage id="loyaltyCampaign.rules.create.action.reload" defaultMessage="Reload" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <CreateRule
        onSubmit={handleCreateSubmit}
        loading={loading}
        error={error}
        companyId={queryCompanyData?.loyaltyCampaign.company.id}
      />
    )
  }
  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage id="loyaltyCampaign.rules.new.title" defaultMessage="New rule" />
        </Heading.H3>
      </FormLayout.Header>

      <FormLayout.Content>
        <Card.Container>{content}</Card.Container>
      </FormLayout.Content>
    </div>
  )
}

export default withLastUrl(NewRuleView)
